import { AddressElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { fetchUserInfo, selectUserInfo } from 'app/store/user';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { useAuth } from 'providers/AuthProvider';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { ATTACH_PAYMENT_METHOD } from 'app/graphql';
import { Button } from 'components/ui/button';
import { Typography } from 'components/ui/typography';
import { Loader2 } from 'lucide-react';
import { Container } from './styles';
import { Routes } from 'config/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { isLegacyBilling } from 'lib/utils';
import { format } from 'date-fns';
import { BillingStatus } from 'app/graphql/generated/admin/graphql';
import { canWrite } from 'app/access';

const Page = () => {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const userState = useAppSelector(selectUserInfo);
  const canEditBilling = canWrite(userState, 'billing');
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPaymentElementComplete, setIsPaymentElementComplete] = useState(false);

  const [attachPaymentMethod] = useMutation(ATTACH_PAYMENT_METHOD, {
    context: { clientName: 'admin' },
  });

  useEffect(() => {
    // We only want to show this page when a developer has no payment method or is on legacy billing
    if (userState?.stripe?.paymentMethod || isLegacyBilling(userState?.billingPeriodSettings)) {
      navigate(location.state?.redirect ?? Routes.Dashboard);
    }
  }, [userState?.stripe?.paymentMethod, navigate]);

  const handleSubmit = async () => {
    if (!canEditBilling) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (!stripe || !elements || !user) {
        setIsSubmitting(false);
        return;
      }
      const submitResult = await elements.submit();
      if (submitResult?.error) {
        // Stripe surfaces validation messages inline in the Elements UI.
        setIsSubmitting(false);
        return;
      }

      const pm = await stripe.createPaymentMethod({
        elements,
        params: { metadata: { developerId: user.uid } },
      });

      if (pm.error) {
        if (pm.error.type === 'validation_error') {
          setIsSubmitting(false);
          return;
        }
        throw new Error(pm.error.message);
      }

      if (!pm.paymentMethod) {
        throw new Error('Stripe did not return a payment method after creation');
      }

      const name = pm.paymentMethod.billing_details.name;
      const firstName = name ? name.split(' ')[0] : user.displayName?.split(' ')[0] ?? '';
      const lastName = name ? name.split(' ')[1] ?? '' : user.displayName?.split(' ')[1] ?? '';

      const res = await attachPaymentMethod({
        variables: {
          input: {
            id: pm.paymentMethod.id,
            billingDetails: {
              firstName,
              lastName,
            },
          },
        },
      });
      if (res.errors) {
        throw new Error(JSON.stringify(res.errors));
      }

      const pendingVerification = res.data?.attachPaymentMethod?.pendingMicrodepositVerification;
      if (pendingVerification) {
        dispatch(fetchUserInfo(user?.uid, user));
        toast.info('Verify your bank account', {
          id: 'bank-verification-pending',
          description:
            'We saved this bank account. Microdeposits take 1-2 business days. This bank account becomes your payment method once verified.',
          duration: Infinity,
          action: {
            label: 'Verify bank account',
            onClick: () => {
              window.open(
                pendingVerification.hostedVerificationUrl,
                '_blank',
                'noopener,noreferrer',
              );
            },
          },
        });
        navigate(Routes.DrawdownSetup);
        return;
      }

      const stripeConfig = res.data?.attachPaymentMethod?.stripeConfig;
      if (!stripeConfig) {
        throw new Error('customer does not exist');
      }
      const paymentMethod = stripeConfig.paymentMethod;

      if (!paymentMethod) {
        throw new Error('payment method does not exist');
      }
      dispatch(fetchUserInfo(user?.uid, user));
      toast.success('Payment method updated', {
        id: 'payment-added-success',
      });
      navigate(Routes.DrawdownSetup);
    } catch (error) {
      Sentry.captureException(new Error('Failed to attach payment method', { cause: error }));
      toast.error('Error adding payment method', {
        id: 'payment-added-failed',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <div>
        <Typography variant="h2" as="h1">
          Enter your payment details
        </Typography>
        <div className="mt-2">
          {userState?.billingPeriodSettings?.__typename === 'BillingSubscription' &&
            userState.billingPeriodSettings.status === BillingStatus.Trial && (
              <Typography variant="small" as="p">
                Your card will be charged{' '}
                <Typography variant="link" asChild>
                  <a href="https://rye.com/pricing" target="_blank" rel="noopener noreferrer">
                    $149 monthly
                  </a>
                </Typography>{' '}
                after your trial ends
                {userState?.billingPeriodSettings?.endDate ? (
                  <>
                    {' '}
                    on{' '}
                    <b>
                      {format(new Date(userState?.billingPeriodSettings?.endDate), 'MMM d, yyyy')}
                    </b>
                  </>
                ) : (
                  ''
                )}
                .
              </Typography>
            )}
          <Typography variant="small" as="p">
            You can cancel anytime by clicking <b>Manage Subscription</b> in the dropdown menu
            above.
          </Typography>
        </div>
      </div>
      <div>
        <Typography variant="small" as="p">
          US bank account: 0.8% processing fee, up to a maximum of $5
        </Typography>
        <Typography variant="small" as="p">
          Card: 2.9% + $0.30 processing fee
        </Typography>
      </div>
      <Typography variant="label" as="div">
        Billing Address
      </Typography>
      <AddressElement
        options={{
          mode: 'billing',
        }}
      />
      <Typography variant="label" as="div">
        Payment Method
      </Typography>
      <PaymentElement
        options={{
          paymentMethodOrder: ['us_bank_account', 'card'],
          fields: { billingDetails: { name: 'never', address: 'never' } },
        }}
        onChange={(event) => setIsPaymentElementComplete(event.complete)}
      />
      <div className="flex w-full justify-end">
        <Button
          disabled={isSubmitting || !isPaymentElementComplete || !canEditBilling}
          onClick={handleSubmit}
        >
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit'}
        </Button>
      </div>
    </Container>
  );
};

export default Page;
